/**
 * Subscription + Affiliate Line Item Tracking
 *
 * Stamps hidden line item properties onto subscription lines at add-to-cart so
 * that downstream systems can identify subscription orders and the affiliate
 * who drove them — on the initial order AND on every recurring Recharge order,
 * which are generated server-side with no browser, cookie, or Impact pixel.
 *
 * Properties stamped on any subscription line (a line carrying a `selling_plan`):
 *   properties[_subscription]        always "true" — flags the line as a
 *                                     subscription for the NetSuite finance feed
 *   properties[_affiliate_irpid]     the affiliate (Impact partner id), if known
 *   properties[_affiliate_clickid]   the precise Impact click (backup/attribution)
 *
 * How it works:
 *   1. Capture — on any page load, read the Impact params from the URL
 *      (`irpid` = partner/affiliate id, `im_ref`/`irclickid` = click id) and
 *      persist them to a first-party cookie (`_ins_affiliate`, 30-day window to
 *      match Impact's `irclickid` cookie).
 *   2. Inject — wrap fetch + XHR so that any `/cart/add` request carrying a
 *      `selling_plan` gets the properties above appended. A capture-phase form
 *      `submit` listener covers the native (non-AJAX) /cart/add fallback too.
 *
 *   3. Promote — once the visitor has granted marketing + sale-of-data consent,
 *      copy the captured click id into Impact's own `irclickid` cookie. The
 *      impact.com Shopify web pixel only saves the click id if consent already
 *      exists when the landing page loads, and it reads `irclickid` at
 *      checkout_completed. A visitor who lands from a partner link and then
 *      accepts the cookie banner would otherwise reach checkout with an empty
 *      click id, and the partner gets no credit.
 *
 * Recharge persists subscription line item properties onto every renewal order,
 * so these values ride along to each recurring charge (Recharge documents this
 * for its own underscore-prefixed properties). The leading underscore keeps them
 * hidden from the customer-facing cart/checkout while still flowing to the order
 * data NetSuite consumes.
 *
 * Loaded ungated (affiliate visitors are typically not logged in) and BEFORE
 * global.js so the fetch/XHR wrappers install before any add-to-cart fires.
 */

(function () {
  "use strict";

  var COOKIE_NAME = "_ins_affiliate";
  var COOKIE_EXPIRY_DAYS = 30; // matches Impact's irclickid cookie window
  var DEBUG_MODE = true;

  // Always stamped on a subscription line so NetSuite can flag subscription
  // orders (initial + recurring) in the financial feed.
  var SUBSCRIPTION_FLAG_KEY = "_subscription";
  var SUBSCRIPTION_FLAG_VALUE = "true";

  // Impact URL params -> our normalized keys.
  var PARAM_IRPID = "irpid"; // partner / affiliate id (only ever in the URL)
  var PARAM_CLICK = ["im_ref", "irclickid"]; // click reference

  function log() {
    if (!DEBUG_MODE) return;
    // eslint-disable-next-line no-console
    console.log.apply(
      console,
      ["[Affiliate Tracking]"].concat([].slice.call(arguments)),
    );
  }

  // ---------------------------------------------------------------------------
  // Cookie helpers
  // ---------------------------------------------------------------------------

  function setCookie(name, value, days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    var hostname = window.location.hostname;
    document.cookie =
      name +
      "=" +
      encodeURIComponent(value) +
      ";expires=" +
      date.toUTCString() +
      ";path=/;domain=." +
      hostname +
      ";SameSite=Lax;Secure";
  }

  function getCookie(name) {
    var nameEQ = name + "=";
    var raw = document.cookie
      .split(";")
      .map(function (c) {
        return c.trim();
      })
      .find(function (c) {
        return c.indexOf(nameEQ) === 0;
      });
    if (!raw) return null;
    return decodeURIComponent(raw.substring(nameEQ.length));
  }

  // ---------------------------------------------------------------------------
  // Capture: read affiliate identity from URL / cookies and persist it
  // ---------------------------------------------------------------------------

  function readStored() {
    var raw = getCookie(COOKIE_NAME);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function getParam(params, key) {
    var v = params.get(key);
    if (v == null) return "";
    return String(v).trim();
  }

  function captureFromUrl() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }

    var irpid = getParam(params, PARAM_IRPID);

    var clickid = "";
    for (var i = 0; i < PARAM_CLICK.length; i++) {
      clickid = getParam(params, PARAM_CLICK[i]);
      if (clickid) break;
    }
    // Impact also drops a first-party `irclickid` cookie; prefer the URL value,
    // fall back to the cookie so we still capture the click on later page views.
    if (!clickid) clickid = getCookie("irclickid") || "";

    // Only (re)write when this visit actually carries an affiliate id. Impact is
    // last-click, so a fresh irpid overwrites any prior attribution.
    if (!irpid) {
      log("no irpid on this page — leaving stored attribution untouched");
      return;
    }

    var data = {
      irpid: irpid,
      clickid: clickid,
      ts: Date.now(),
    };

    setCookie(COOKIE_NAME, JSON.stringify(data), COOKIE_EXPIRY_DAYS);
    log("captured affiliate attribution", data);
  }

  /**
   * Returns the affiliate properties to stamp, or null if we have nothing to
   * attribute. Reads the freshest values available at injection time.
   */
  function affiliateProperties() {
    var stored = readStored() || {};
    var irpid = stored.irpid || "";
    var clickid = stored.clickid || getCookie("irclickid") || "";

    if (!irpid && !clickid) return null;

    var props = {};
    if (irpid) props["_affiliate_irpid"] = irpid;
    if (clickid) props["_affiliate_clickid"] = clickid;
    return props;
  }

  /**
   * The full set of properties to stamp on a subscription line: always the
   * subscription flag, plus affiliate attribution when we have it.
   */
  function subscriptionLineProperties() {
    var props = {};
    props[SUBSCRIPTION_FLAG_KEY] = SUBSCRIPTION_FLAG_VALUE;
    var affiliate = affiliateProperties();
    if (affiliate) {
      Object.keys(affiliate).forEach(function (key) {
        props[key] = affiliate[key];
      });
    }
    return props;
  }

  // ---------------------------------------------------------------------------
  // Inject: append affiliate properties to subscription /cart/add requests
  // ---------------------------------------------------------------------------

  function isCartAddUrl(url) {
    if (!url) return false;
    // Fast path: skip URL parsing for the vast majority of requests (analytics
    // beacons, etc.) that have nothing to do with the cart.
    if (String(url).indexOf("/cart/add") === -1) return false;
    try {
      // Handles relative, locale-prefixed, and absolute URLs.
      var path = new URL(url, window.location.origin).pathname;
      return /\/cart\/add(\.js)?$/.test(path);
    } catch (e) {
      return /\/cart\/add(\.js)?(\?|$)/.test(String(url));
    }
  }

  function hasValue(v) {
    return v != null && String(v).length > 0;
  }

  // -- FormData bodies (the theme's product-form uses this) -------------------

  function injectIntoFormData(formData) {
    if (!hasValue(formData.get("selling_plan"))) return false;
    var props = subscriptionLineProperties();

    var changed = false;
    Object.keys(props).forEach(function (key) {
      var field = "properties[" + key + "]";
      if (!formData.has(field)) {
        formData.append(field, props[key]);
        changed = true;
      }
    });
    if (changed) log("stamped FormData add-to-cart", props);
    return changed;
  }

  // -- URL-encoded string bodies ----------------------------------------------

  function injectIntoUrlEncoded(bodyStr) {
    var params;
    try {
      params = new URLSearchParams(bodyStr);
    } catch (e) {
      return bodyStr;
    }
    if (!hasValue(params.get("selling_plan"))) return bodyStr;
    var props = subscriptionLineProperties();

    var changed = false;
    Object.keys(props).forEach(function (key) {
      var field = "properties[" + key + "]";
      if (!params.has(field)) {
        params.append(field, props[key]);
        changed = true;
      }
    });
    if (changed) log("stamped url-encoded add-to-cart", props);
    return changed ? params.toString() : bodyStr;
  }

  // -- JSON bodies (Shopify /cart/add.js single item or { items: [...] }) ------

  function injectIntoJsonObject(obj) {
    var props = subscriptionLineProperties();

    function stampItem(item) {
      if (!item || typeof item !== "object") return false;
      if (!hasValue(item.selling_plan)) return false;
      item.properties = item.properties || {};
      var changed = false;
      Object.keys(props).forEach(function (key) {
        if (item.properties[key] == null) {
          item.properties[key] = props[key];
          changed = true;
        }
      });
      return changed;
    }

    var changedAny = false;
    if (Array.isArray(obj.items)) {
      obj.items.forEach(function (item) {
        if (stampItem(item)) changedAny = true;
      });
    } else {
      changedAny = stampItem(obj);
    }
    if (changedAny) log("stamped JSON add-to-cart", props);
    return changedAny;
  }

  function injectIntoStringBody(bodyStr) {
    var trimmed = bodyStr.trim();
    if (trimmed.charAt(0) === "{" || trimmed.charAt(0) === "[") {
      try {
        var obj = JSON.parse(bodyStr);
        if (injectIntoJsonObject(obj)) return JSON.stringify(obj);
        return bodyStr;
      } catch (e) {
        // not valid JSON, fall through to url-encoded handling
      }
    }
    return injectIntoUrlEncoded(bodyStr);
  }

  // -- fetch wrapper ----------------------------------------------------------

  function wrapFetch() {
    if (!window.fetch || window.fetch.__insAffiliateWrapped) return;
    var originalFetch = window.fetch;

    var wrapped = function (input, init) {
      try {
        var url = typeof input === "string" ? input : input && input.url;
        if (isCartAddUrl(url) && init && init.body != null) {
          var body = init.body;
          if (typeof FormData !== "undefined" && body instanceof FormData) {
            injectIntoFormData(body);
          } else if (
            typeof URLSearchParams !== "undefined" &&
            body instanceof URLSearchParams
          ) {
            var resStr = injectIntoUrlEncoded(body.toString());
            init.body = new URLSearchParams(resStr);
          } else if (typeof body === "string") {
            init.body = injectIntoStringBody(body);
          }
        }
      } catch (e) {
        log("fetch wrapper error (passing through):", e);
      }
      return originalFetch.apply(this, arguments);
    };

    wrapped.__insAffiliateWrapped = true;
    window.fetch = wrapped;
  }

  // -- XHR wrapper (covers jQuery.ajax / Recharge-style adds) -----------------

  function wrapXhr() {
    if (typeof XMLHttpRequest === "undefined") return;
    var proto = XMLHttpRequest.prototype;
    if (proto.send.__insAffiliateWrapped) return;

    var originalOpen = proto.open;
    var originalSend = proto.send;

    proto.open = function (method, url) {
      this.__insAffiliateIsCartAdd = isCartAddUrl(url);
      return originalOpen.apply(this, arguments);
    };

    var wrappedSend = function (body) {
      try {
        if (this.__insAffiliateIsCartAdd && body != null) {
          if (typeof FormData !== "undefined" && body instanceof FormData) {
            injectIntoFormData(body);
          } else if (
            typeof URLSearchParams !== "undefined" &&
            body instanceof URLSearchParams
          ) {
            arguments[0] = new URLSearchParams(
              injectIntoUrlEncoded(body.toString()),
            );
            return originalSend.apply(this, arguments);
          } else if (typeof body === "string") {
            arguments[0] = injectIntoStringBody(body);
            return originalSend.apply(this, arguments);
          }
        }
      } catch (e) {
        log("xhr wrapper error (passing through):", e);
      }
      return originalSend.apply(this, arguments);
    };

    wrappedSend.__insAffiliateWrapped = true;
    proto.send = wrappedSend;
  }

  // -- Native form submit safety net ------------------------------------------
  // The theme's product form falls back to a native (non-AJAX) POST to
  // /cart/add on the cart page or when the cart drawer is disabled. The
  // fetch/XHR wrappers can't see that submit, so stamp hidden inputs onto the
  // form instead. This is dedupe-safe with the AJAX path: it runs in the
  // capture phase (before the theme builds its FormData), and the fetch/XHR
  // wrappers skip fields that are already present.

  var INJECTED_ATTR = "data-ins-affiliate";

  function isCartAddForm(form) {
    var action = form.getAttribute("action");
    if (!action) return false;
    try {
      return /\/cart\/add$/.test(
        new URL(action, window.location.origin).pathname,
      );
    } catch (e) {
      return /\/cart\/add(\?|$)/.test(action);
    }
  }

  function injectIntoForm(form) {
    // Always clear our prior inputs first, so toggling subscribe -> one-time on
    // the same form never leaves a stale subscription flag on the line.
    var prior = form.querySelectorAll("input[" + INJECTED_ATTR + "]");
    Array.prototype.forEach.call(prior, function (node) {
      if (node.parentNode) node.parentNode.removeChild(node);
    });

    var sellingPlanField = form.querySelector('[name="selling_plan"]');
    var sellingPlan = sellingPlanField ? sellingPlanField.value : "";
    if (!hasValue(sellingPlan)) return;

    var props = subscriptionLineProperties();
    Object.keys(props).forEach(function (key) {
      var field = "properties[" + key + "]";
      // Respect any property already present in the form markup.
      if (form.querySelector('[name="' + field + '"]')) return;
      var input = document.createElement("input");
      input.type = "hidden";
      input.name = field;
      input.value = props[key];
      input.setAttribute(INJECTED_ATTR, "1");
      form.appendChild(input);
    });
    log("stamped native form submit", props);
  }

  function wrapFormSubmit() {
    document.addEventListener(
      "submit",
      function (evt) {
        try {
          var form = evt.target;
          if (form && form.tagName === "FORM" && isCartAddForm(form)) {
            injectIntoForm(form);
          }
        } catch (e) {
          log("form submit wrapper error (passing through):", e);
        }
      },
      true,
    );
  }

  // ---------------------------------------------------------------------------
  // Promote: hand the click id to Impact's pixel once consent allows it
  // ---------------------------------------------------------------------------

  var IMPACT_COOKIE_NAME = "irclickid";
  var CONSENT_POLL_MS = 100;
  var CONSENT_POLL_MAX = 100; // 10s for consent-tracking-api to load

  // Mirrors the impact.com pixel's own gate: it only tracks when both
  // marketingAllowed and saleOfDataAllowed are true.
  function impactConsentGranted() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || !cp.marketingAllowed || !cp.saleOfDataAllowed) return false;
    try {
      return cp.marketingAllowed() === true && cp.saleOfDataAllowed() === true;
    } catch (e) {
      return false;
    }
  }

  // Written host-only and unencoded, matching how the pixel sets the cookie,
  // so both writers share one cookie and the pixel reads the raw value.
  function setImpactCookie(value) {
    var date = new Date();
    date.setTime(date.getTime() + COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
    document.cookie =
      IMPACT_COOKIE_NAME +
      "=" +
      value +
      ";expires=" +
      date.toUTCString() +
      ";path=/;SameSite=Lax;Secure";
  }

  function promoteClickId() {
    if (!impactConsentGranted()) return;
    var stored = readStored();
    var clickid = stored && stored.clickid;
    if (!clickid || !/^[A-Za-z0-9:_.~-]+$/.test(clickid)) return;
    // The pixel owns this cookie once it has consent; only fill the gap.
    if (getCookie(IMPACT_COOKIE_NAME)) return;
    setImpactCookie(clickid);
    log("promoted click id to irclickid cookie", clickid);
  }

  function watchConsent() {
    document.addEventListener("visitorConsentCollected", promoteClickId);
    document.addEventListener("trackingConsentAccepted", promoteClickId);

    var attempts = 0;
    (function poll() {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp) {
        promoteClickId();
        return;
      }
      if (++attempts < CONSENT_POLL_MAX) setTimeout(poll, CONSENT_POLL_MS);
    })();
  }

  // ---------------------------------------------------------------------------
  // Init — install wrappers immediately, capture as soon as possible.
  // ---------------------------------------------------------------------------

  wrapFetch();
  wrapXhr();
  wrapFormSubmit();
  captureFromUrl();
  watchConsent();

  // Expose for debugging / manual verification.
  window.__insAffiliate = {
    get: affiliateProperties,
    subscriptionProps: subscriptionLineProperties,
    stored: readStored,
  };
})();
